import React, {useEffect} from 'react';
import {Score} from "./Score";
import {
  Button,
  Dialog, DialogActions,
  DialogContent,
  DialogTitle,
  IconButton, ListSubheader
} from "@mui/material";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, {SelectChangeEvent} from '@mui/material/Select';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import MuiInput from '@mui/material/Input';
import VolumeUp from '@mui/icons-material/VolumeUp';

import {getInstruments} from "./instruments";

import * as Tone from "tone";
import {createScale} from "./createScale";
import {MidiNote, Scale, ScaleNote} from "./types";
import {getNote, parseNote, getOctave} from "./notes";
import {MembraneSynth, Sequence, Synth} from "tone";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";

import {t} from "./translations";
import {Instrument} from "tone/build/esm/instrument/Instrument";
import { useWakeLock } from 'react-screen-wake-lock';



export interface IPracticeAppProps {

}


const PracticeApp = (props: IPracticeAppProps) => {
  const [open, setOpen] = React.useState(true);

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isPaused, setIsPaused] = React.useState(false);

  const [bpm, setBpm] = React.useState(80);
  const [volume, setVolume] = React.useState(30);

  const sequenceRef = React.useRef<Sequence>(null);
  const synthRef = React.useRef<Instrument<any>>(null);
  const metronomeRef = React.useRef<Instrument<any>>(null);

  const [scaleInfo, setScaleInfo] = React.useState<Scale>();


  const [notes, setNotes] = React.useState([
    'g3', 'd4', 'e4', 'd4',
    'a4', 'd4', 'e4', 'd4',
    'a4', 'a4', 'b4', 'a4',
    'd4', 'e4', 'g3'
  ]);




  const [staves, setStaves] = React.useState([
    ['gb/3', 'd4', 'eb/4', 'd4'],
    ['a4', 'd4', 'e4', 'd4'],
    ['a4', 'a4', 'b4', 'a4'],
    ['d4', 'e4', 'G#/3', ['b/4', "4r"]],
  ]);

  const [instrument, setInstrument] = React.useState('clarinet');
  const [skillLevel, setSkillLevel] = React.useState('intermediate');

  const [rangeBottom, setRangeBottom] = React.useState<MidiNote>(
    {
      semitones: 4,
      note: 'E',
      flats: 0,
      sharps: 0,
      midi: (3 + 1) * 12 + 4, // E3
    });
  const [rangeTop, setRangeTop] = React.useState({
    semitones: 4,
    note: 'E',
    flats: 0,
    sharps: 0,
    midi: (5 + 1) * 12 + 4, // E6
  });

  const [transpose, setTranspose] = React.useState(0);

  const [schema, setSchema] = React.useState([1]);
  const [schemaMachineName, setSchemaMachineName] = React.useState('[1]');

  const schemas = [];

  const [key, setKey] = React.useState('C');
  const [scale, setScale] = React.useState('major');


  const { isSupported, released, request, release } = useWakeLock({
    onRequest: () => console.log('Screen Wake Lock: requested!'),
    onError: () => console.log('An error happened 💥'),
    onRelease: () => console.log('Screen Wake Lock: released!'),
    reacquireOnPageVisible: true,
  });

  function generateMidis(scaleNotes: ScaleNote[], index: number): number[] {
    let midiOctave = Math.floor(rangeBottom.midi / 12);
    let practiceNote = scaleNotes[index];
    if (rangeBottom.semitones + rangeBottom.flats - rangeBottom.sharps
      > practiceNote.semitones + practiceNote.flats - practiceNote.sharps) {
      midiOctave++;
    }

    let midi = midiOctave * 12 + practiceNote.semitones;
    let midis: number[] = [];
    while (midi <= rangeTop.midi) {
      midis.push(midi);
      const nextIndex = (index + 1) % scaleNotes.length;
      let semitonesDiff = scaleNotes[nextIndex].semitones - scaleNotes[index].semitones;
      if (semitonesDiff < 0) {
        semitonesDiff += 12;
      }
      index = nextIndex;
      midi += semitonesDiff;
    }
    return midis;
  }

  function generatePracticeMidis(midis: number[], startIndex: number, minIndex: number, maxIndex: number, direction = 1): number[] {
    let index = startIndex;
    let midi = midis[index];

    let schemaIndex = 0;
    let practiceMidis: number[] = [];
    let practiceStave: number[] = [];
    while (index < maxIndex && index >= minIndex) {
      practiceStave.push(midis[index]);
      index = index + direction * schema[schemaIndex];
      schemaIndex = (schemaIndex + 1) % schema.length;
      if (schemaIndex == 0) {
        practiceMidis.push(...practiceStave);
        practiceStave = [];
      }
    }
    return practiceMidis;
  }

  function generatePracticeMidisUpDownUp(midis: number[], startIndex: number): number[] {
    let practiceUpMidis = generatePracticeMidis(midis, startIndex, 0, midis.length, 1);

    let startDownIndex = midis.findIndex((midi) => midi == practiceUpMidis[practiceUpMidis.length - 1]);
    if (startDownIndex == midis.length - 1) {
      startDownIndex--;
    } else if (startDownIndex < midis.length - 1) {
      startDownIndex++;
    }
    let practiceDownMidis = generatePracticeMidis(midis, startDownIndex, 0, midis.length, -1);

    let startUpIndex = midis.findIndex((midi) => midi == practiceDownMidis[practiceDownMidis.length - 1]);
    if (startUpIndex == 0) {
      startUpIndex++;
    } else if (startUpIndex > 1) {
      startUpIndex--;
    }
    let practiceUpAgainMidis = generatePracticeMidis(midis, startUpIndex, 0, startIndex + 1, 1);


    let practiceMidis = [...practiceUpMidis, ...practiceDownMidis, ...practiceUpAgainMidis];

    // const lastMidi = practiceMidis[practiceMidis.length-1];
    // const lastNote = getNote(lastMidi);
    // // if last not ist not the tonic
    // if (lastNote.note != key) {
    //   let closestTonic = 0;
    //   let octaves = [1,2,3,4,5,6,7];
    //   octaves.forEach((octave) => {
    //     const candidateResult = parseNote(key + octave);
    //     if ('data' in candidateResult) {
    //       const candidate = candidateResult.data;
    //       if (Math.abs(candidate.midi - lastMidi) < Math.abs(closestTonic - lastMidi)) {
    //         closestTonic = candidate.midi;
    //       }
    //     }
    //   });
    //   practiceMidis = [...practiceMidis, closestTonic];
    // }


    return practiceMidis;
  }

  function generateNotesToDisplay(midis: number[], scaleNotes: ScaleNote[]): string[] {
    let octaveSeparator = '/';
    let notes: string[] = [];
    midis.forEach((midi) => {
      let semitones = midi % 12;
      let scaleNote = scaleNotes.find((scaleNote) => scaleNote.semitones == semitones);

      let note = scaleNote.note;
      note = note.replaceAll('♭', 'b');
      note = note.replaceAll('♯', '#');
      note = note.toLowerCase();

      let octave = Math.floor(((midi - 12) + scaleNote.flats - scaleNote.sharps) / 12);
      note = note + octaveSeparator + octave;
      notes.push(note);
    });
    return notes;
  }

  function generateNotesToPlayBack(midis: number[]): string[] {
    let octaveSeparator = '';
    let notes: string[] = [];
    midis.forEach((midi) => {
      midi = midi + transpose;
      let midiNote = getNote(midi);
      let note = midiNote.note;
      note = note.replaceAll('♭', 'b');
      note = note.replaceAll('♯', '#');
      note = note.toLowerCase();

      let octave = Math.floor(((midi - 12) + midiNote.flats - midiNote.sharps) / 12);
      note = note + octaveSeparator + octave;
      notes.push(note);
    });
    return notes;
  }

  useEffect(() => {
    if (!synthRef.current) {
      // synthRef.current = new Tone.Synth({
      //   volume: 5,
      //   oscillator: {
      //     type: 'sine'
      //   }}).toDestination();

      synthRef.current = new Tone.Synth().toDestination();

      Tone.Master.volume.value = -10;

      metronomeRef.current = new Tone.Synth({
        oscillator: {
          type: 'sine'
        },
        envelope: {
          attack: 0,
          decay: 0.1,
          sustain: 0,
          release: 0.1,
        }
      }).toDestination();


      // const metronome.current =  new Tone.Synth({
      //   oscillator: {type: 'square'},
      //   envelope: {attack: 0.0001, decay: 0.001, sustain: 0, release: 0.1},
      // }).toDestination();
    }

  }, []);

  useEffect(() => {
    Tone.Transport.bpm.value = bpm;
  }, [bpm])

  useEffect(() => {
    const instruments = getInstruments();
    const instrumentData = instruments.find((instr) => instr.name == instrument);


    const instrumentSkillLevel = instrumentData.skillLevels.find((sl) => sl.name == skillLevel)
    const bottomNoteStr = instrumentSkillLevel.bottom;
    const bottom = parseNote(bottomNoteStr);
    if ('data' in bottom) {
      setRangeBottom(bottom.data);
    }

    const topNoteStr = instrumentSkillLevel.top;
    const top = parseNote(topNoteStr);
    if ('data' in top) {
      setRangeTop(top.data);
    }

    setTranspose(instrumentData.transpose);
  }, [instrument, skillLevel])





  useEffect(() => {
    const resultScale = createScale(key, scale);
    if ('data' in resultScale) {
      const scaleInfo = resultScale.data;
      setScaleInfo(scaleInfo);
      const scaleNotes = scaleInfo.scaleNotes;

      console.log('scaleInfo', scaleInfo);

      let startNote = scaleNotes[0];
      let startIndex = 0;
      scaleNotes.forEach((scaleNote, index) => {
        let diff = scaleNote.semitones - rangeBottom.semitones
        let diffStartNote = startNote.semitones - rangeBottom.semitones
        if ((diff >= 0 && diffStartNote < 0) || (diff >= 0 && diff < diffStartNote)) {
          startNote = scaleNote;
          startIndex = index;
        }
      })

      //startIndex = 0;
      let midis = generateMidis(scaleNotes, startIndex);

      let practiceStartIndex = midis.findIndex((midi) => midi % 12 == scaleNotes[0].semitones)
      let practiceMidis = generatePracticeMidisUpDownUp(midis, practiceStartIndex);

      let newNotes = generateNotesToPlayBack(practiceMidis);
      setNotes(newNotes);

      const newNotesForDisplay = generateNotesToDisplay(practiceMidis, scaleNotes);
      let [list, chunkSize] = [newNotesForDisplay, 4];
      const newStaves = [...Array(Math.ceil(list.length / chunkSize))].map(_ => list.splice(0, chunkSize));
      setStaves(newStaves);


      console.log('new notes', newNotes);
      console.log('new staves', newStaves);
    } else {
      console.log("scale error", scale);
    }

  }, [key, scale, schema, transpose, rangeBottom, rangeTop])

  const handleSchemaChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    const mapping = {
      "[1]": [1],
      "[2,-1]": [2, -1],
      "[1,1,1,-2]": [1, 1, 1, -2],
      "[1,1,1,1,-1,-1,-1, 1]": [1,1,1,1,-1,-1,-1, 1],
    }
    setSchemaMachineName(value);
    setSchema(mapping[value]);
  };

  const handleScaleChange = (event: SelectChangeEvent) => {
    setScale(event.target.value as string);
  };


  const handlePlayButtonClick = () => {
    console.log("isSupported", isSupported);
    if (released && isSupported) {
      request();
    }
    if (isPaused) {
      Tone.Transport.start();
      if (sequenceRef.current) {
        sequenceRef.current.start();
      }
      setIsPaused(false);
      setIsPlaying(true);
    }
    if (isPlaying) {
      Tone.Transport.pause();
      if (sequenceRef.current) {
        sequenceRef.current.stop();
        sequenceRef.current.dispose();
      }
      setIsPaused(true);
      setIsPlaying(false);
    } else {

      // Wenn Tone.js noch nicht gestartet ist, starte es
      if (Tone.getContext().state !== 'running') {
        Tone.start();
      }

      setIsPlaying(true);

      const notesIndexes = Array.from({length: notes.length + 4}, (_, i) => i);
      // Erstelle eine Sequenz mit Tone.js
      sequenceRef.current = new Tone.Sequence(
        (time, index) => {
          if (index < 4) {
            metronomeRef.current.triggerAttackRelease('a4', '16n', time);
          } else {
            synthRef.current.triggerAttackRelease(notes[index - 4], '4n', time);
          }


          if (index - 4 >= notes.length - 1) {
            Tone.Transport.stop();
            sequenceRef.current.dispose();
            setIsPaused(false);
            setIsPlaying(false);
          }
        },
        notesIndexes,
        '4n' // Viertelnoten-Tempo, kann angepasst werden
      );
      sequenceRef.current.loop = false;

      Tone.Transport.bpm.value = bpm;
      Tone.Transport.start();
      // Starte die Sequenz
      sequenceRef.current.start(0);
    }

  };

  const handleStopButtonClick = () => {
    if (!released && isSupported) {
      release();
    }

    Tone.Transport.stop();
    if (sequenceRef.current) {
      sequenceRef.current.dispose();
    }
    setIsPaused(false);
    setIsPlaying(false);
  }


  // Optional: Funktion zum Stoppen und Zurücksetzen
  const stopSequence = () => {
    if (sequenceRef.current) {
      sequenceRef.current.stop();
      sequenceRef.current.dispose();
      sequenceRef.current = null;
    }
    setIsPlaying(false);
  };

  const handleVolumeSliderChange = (event: Event, newValue: number) => {
    setVolume(newValue);
  };

  const handleVolumeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(event.target.value === '' ? 0 : Number(event.target.value));
  };

  const handleVolumeBlur = () => {
    if (volume < 0) {
      setVolume(0);
    } else if (volume > 100) {
      setVolume(100);
    }
  };

  const handleBpmSliderChange = (event: Event, newValue: number) => {
    setBpm(newValue);
  };

  const handleBpmInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBpm(event.target.value === '' ? 80 : Number(event.target.value));
  };

  const handleBpmBlur = () => {
    if (bpm < 10) {
      setBpm(10);
    } else if (bpm > 200) {
      setBpm(200);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOk = () => {

    setOpen(false);
  };

  return (
    <>
      <Box sx={{
        marginBottom: 4,
        fontSize: 20
      }}>{t(instrument)} ({t(skillLevel)}: {rangeBottom.note}{getOctave(rangeBottom)} bis {rangeTop.note}{getOctave(rangeTop)}) <IconButton
        aria-label="open"
        onClick={handleOpen}
        sx={(theme) => ({
          color: theme.palette.grey[500],
        })}
      >
        <SettingsIcon/>
      </IconButton></Box>


      <Box sx={{display: "flex", gap: 2, marginBottom: 4}}>
        <Box sx={{flexGrow: 1000, maxWidth: 250}} >
          <Slider
            value={typeof bpm === 'number' ? bpm : 80}
            onChange={handleBpmSliderChange}
            aria-labelledby="input-slider"
            min={10}
            max={200}
          />
        </Box>
        <Box>
          <MuiInput
            value={bpm}
            size="small"
            onChange={handleBpmInputChange}
            onBlur={handleBpmBlur}
            endAdornment={<InputAdornment
              position="end">bpm</InputAdornment>}
            inputProps={{
              step: 5,
              min: 10,
              max: 200,
              type: 'number',
              'aria-labelledby': 'input-slider',
            }}
          />
        </Box>
        <Box sx={{flexGrow: 1}} >
        </Box>
        <Box sx={(theme) => ({ display: "flex", border: "1px solid", borderColor: theme.palette.grey[500], borderRadius: 2 })}>
          <Box
            sx={(theme) => ({ borderRight: "1px solid", padding: "0 5px", borderColor: theme.palette.grey[500] })}>
            <IconButton
              aria-label="play or pause"
              onClick={handlePlayButtonClick}
            >
              {isPlaying &&
                <PauseIcon></PauseIcon>
              }
              {!isPlaying &&
                <PlayArrowIcon></PlayArrowIcon>
              }
            </IconButton>
          </Box>

          <Box sx={{ padding: "0 5px" }} >
            <IconButton
              aria-label="stop"
              onClick={handleStopButtonClick}
              disabled={!(isPlaying || isPaused)}
              color={"primary"}
            >
              <StopIcon></StopIcon>
            </IconButton>
          </Box>

        </Box>
      </Box>

      <Box sx={{display: "flex", gap: 2, marginBottom: 2}}>
        <FormControl
          sx={{flexGrow: 1}}
        >
          <Select
            sx={{width: "100%"}}
            variant="standard"
            labelId="key-label"
            id="key-select"
            value={key}
            label="Tonart"
            disabled={isPlaying || isPaused}
            onChange={(event: SelectChangeEvent) => {
              setKey(event.target.value as string);
            }}
          >
            <MenuItem value={'Db'}>D♭</MenuItem>
            <MenuItem value={'Ab'}>A♭</MenuItem>
            <MenuItem value={'Eb'}>E♭</MenuItem>
            <MenuItem value={'Bb'}>B♭</MenuItem>
            <MenuItem value={'F'}>F</MenuItem>
            <MenuItem value={'C'}>C</MenuItem>
            <MenuItem value={'G'}>G</MenuItem>
            <MenuItem value={'D'}>D</MenuItem>
            <MenuItem value={'A'}>A</MenuItem>
            <MenuItem value={'E'}>E</MenuItem>
            <MenuItem value={'F#'}>F#</MenuItem>
          </Select>
        </FormControl>
        <FormControl
          sx={{flexGrow: 1}}
        >

          <Select
            sx={{width: "100%"}}
            variant="standard"
            labelId="scale-label"
            id="scale-select"
            value={scale}
            label="Tonleiter"
            disabled={isPlaying || isPaused}
            onChange={handleScaleChange}
          >
            <ListSubheader>Tonleitern</ListSubheader>
            <MenuItem value={'major'}>Dur</MenuItem>
            <MenuItem value={'minor'}>Moll</MenuItem>
            <ListSubheader>Akkorde</ListSubheader>
            <MenuItem value={'Major Chord'}>Dur-Akkord</MenuItem>
            <MenuItem value={'Major 7th Chord'}>Major 7th Chord</MenuItem>
            <MenuItem value={'Minor Chord'}>Moll-Akkord</MenuItem>
            <MenuItem value={'Minor 7th Chord'}>Minor 7th Chord</MenuItem>
            <MenuItem value={'Dominant 7th Chord'}>Dominant 7th Chord</MenuItem>
            <MenuItem value={'Diminished 7th Chord'}>Diminished 7th Chord</MenuItem>
            <MenuItem value={'Augmented 7th Chord'}>Augmented 7th Chord</MenuItem>
            <ListSubheader>Pentatonic</ListSubheader>
            <MenuItem value={'Major Pentatonic'}>Pentatonic (Dur)</MenuItem>
            <MenuItem value={'Minor Pentatonic'}>Pentatonic (Moll)</MenuItem>
            <ListSubheader>Blues</ListSubheader>
            <MenuItem value={'Major Pentatonic Blues'}>Bluestonleiter (Dur)</MenuItem>
            <MenuItem value={'Minor Pentatonic Blues'}>Bluestonleiter (Moll)</MenuItem>
            <ListSubheader>Moll</ListSubheader>
            <MenuItem value={'Harmonic Minor'}>Harmonic Minor</MenuItem>
            <MenuItem value={'Melodic Minor Ascending'}>Melodic Minor Ascending</MenuItem>
            <ListSubheader>Modi</ListSubheader>
            <MenuItem value={'Ionian'}>Ionian</MenuItem>
            <MenuItem value={'Dorian'}>Dorian</MenuItem>
            <MenuItem value={'Phrygian'}>Phrygian</MenuItem>
            <MenuItem value={'Lydian'}>Lydian</MenuItem>
            <MenuItem value={'Mixolydian'}>Mixolydian</MenuItem>
            <MenuItem value={'Locrian'}>Locrian</MenuItem>

            <ListSubheader>Weitere</ListSubheader>
            <MenuItem value={'Chromatic'}>Chromatisch</MenuItem>








            <MenuItem value={'Whole Tone'}>Whole Tone</MenuItem>
          </Select>
        </FormControl>
        <FormControl
          sx={{flexGrow: 1}}
        >

          <Select
            sx={{width: "100%"}}
            variant="standard"
            labelId="schema-label"
            id="schema-select"
            value={schemaMachineName}
            label="Übung"
            disabled={isPlaying || isPaused}
            onChange={handleSchemaChange}
          >
            <MenuItem value={"[1]"}>Tonleiter (↑, ↑, ↑, ↑)</MenuItem>
            <MenuItem value={"[2,-1]"}>↑↑, ↓, ↑↑, ↓</MenuItem>
            <MenuItem value={"[1,1,1,-2]"}>↑, ↑, ↑, ↓↓</MenuItem>
            <MenuItem value={"[1,1,1,1,-1,-1,-1, 1]"}>↑, ↑, ↑, ↑, ↓, ↓, ↓, ↑</MenuItem>
          </Select>
        </FormControl>

      </Box>
      <Box sx={(theme) => ({
        color: theme.palette.grey[600],
        fontSize: 18,

      })}>
        <Box component="span"
             sx={{ paddingRight: 2}}
        >
        {scaleInfo?.key} {scaleInfo?.scale}: {scaleInfo?.scaleNotes.map((scaleNote) => scaleNote.note).join(', ')}
        </Box>
        <Box component="span"
              sx={{ fontSize: 16}}
        >
          ({scaleInfo?.intervals && Array.from(scaleInfo?.intervals, (entry) => entry.interval)?.join(', ')})
        </Box>

      </Box>


      {/*<FormControl >*/}
      {/*  <InputLabel id="transpose-label">Transponieren</InputLabel>*/}
      {/*  <Select<number>*/}
      {/*    labelId="transpose-label"*/}
      {/*    id="transpose-select"*/}
      {/*    value={transpose}*/}
      {/*    label="Transponieren"*/}
      {/*    disabled={isPlaying || isPaused}*/}
      {/*    onChange={(event: SelectChangeEvent<number>) => {*/}
      {/*      setTranspose(event.target.value as number);*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <MenuItem value={0}>C</MenuItem>*/}
      {/*    <MenuItem value={-2}>B♭</MenuItem>*/}
      {/*    <MenuItem value={-9}>E♭</MenuItem>*/}
      {/*  </Select>*/}
      {/*</FormControl>*/}


      <Box sx={{ overflow: "hidden"}}>
        <Score staves={staves}></Score>
      </Box>


      <Dialog onClose={handleClose} open={open}>
        <DialogTitle>Einstellungen</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon/>
        </IconButton>

        <DialogContent dividers>
          <Box sx={{marginBottom: 2}}>Einstellungen für dein Instrument und dein
            Level.</Box>
          <Grid container spacing={2} sx={{alignItems: 'center'}}>
            <FormControl variant={"outlined"} sx={{width: "100%"}}>
              <InputLabel id="instrument-label">Instrument</InputLabel>
              <Select
                labelId="instrument-label"
                id="instrument-select"
                value={instrument}
                label="instrument"
                onChange={(event: SelectChangeEvent) => {
                  setInstrument(event.target.value as string);
                }}
              >
                <MenuItem value={"clarinet"}>Klarinette</MenuItem>
                <MenuItem value={"altosax"}>Alt-Saxophon</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant={"outlined"} sx={{width: "100%"}}>

              <InputLabel id="skilllevel-label">Level</InputLabel>
              <Select
                labelId="skilllevel-label"
                id="skilllevel-select"
                value={skillLevel}
                label="skilllevel"
                onChange={(event: SelectChangeEvent) => {
                  setSkillLevel(event.target.value as string);
                }}
              >
                <MenuItem value={"beginner"}>Anfänger</MenuItem>
                <MenuItem value={"intermediate"}>Fortgeschritten</MenuItem>
                <MenuItem value={"professional"}>Profi</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOk}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <p>Screen wake lock support: {isSupported ? "true" : "false"}, Screen released: {released ? "true" : "false"}</p>
    </>
  )
    ;
};

export default PracticeApp;
